import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  AppForm,
  AppRadio,
  AppRadioGroup,
  AppFormLabel,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';

import type { DmOccupationType, DmReasonType } from '@/hooks/main/useMainPatientDmInfo';
import type { DmInfoFormData } from '@/schemas/patientDmInfoFormSchema';

export type { DmInfoFormData as DmInfoFormState } from '@/schemas/patientDmInfoFormSchema';

type Props = {
  occupationTypes: DmOccupationType[];
  reasonTypes: DmReasonType[];
  hasVisitAddress: boolean;
};

const PatientDmInfoSection: React.FC<Props> = ({
  occupationTypes,
  reasonTypes,
  hasVisitAddress,
}) => {
  const { control, watch, setValue, clearErrors } = useFormContext<DmInfoFormData>();

  const hasChildren = watch('has_children');
  const childrenNumDisabled = hasChildren !== '1';

  const freeKeys = useMemo(
    () =>
      [
        ['01', 'free01'],
        ['02', 'free02'],
        ['03', 'free03'],
        ['04', 'free04'],
        ['05', 'free05'],
        ['06', 'free06'],
        ['07', 'free07'],
        ['08', 'free08'],
        ['09', 'free09'],
        ['10', 'free10'],
      ] as const,
    []
  );

  return (
    <CollapsibleCard
      title="DM情報"
      className="mb-0"
      extraHeaderContent={<span className="text-xs">閉じる</span>}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex items-start gap-4">
          <div className="w-32 pt-1 text-sm text-gray-700">DM発送</div>
          <div className="w-full max-w-xl">
            <Controller
              name="is_enable_dm"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  className="w-full pt-1"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                >
                  <AppRadio value="1">可</AppRadio>
                  <AppRadio value="0">不可</AppRadio>
                </AppRadioGroup>
              )}
            />
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 pt-1 text-sm text-gray-700">宛先の選択</div>
          <Controller
            name="dm_address_type"
            control={control}
            render={({ field }) => (
              <AppRadioGroup
                className="w-full max-w-xl pt-1"
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
              >
                <AppRadio value="1">保険証の住所</AppRadio>
                <AppRadio value="2" disabled={!hasVisitAddress}>
                  訪問住所
                </AppRadio>
              </AppRadioGroup>
            )}
          />
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 pt-1 text-sm text-gray-700">配偶者</div>
          <Controller
            name="has_spouse"
            control={control}
            render={({ field }) => (
              <AppRadioGroup
                className="w-full max-w-xl pt-1"
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
              >
                <AppRadio value="1">有り</AppRadio>
                <AppRadio value="0">無し</AppRadio>
              </AppRadioGroup>
            )}
          />
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 pt-2.5 text-sm text-gray-700">子供</div>
          <div className="flex items-start w-full max-w-xl gap-4">
            <Controller
              name="has_children"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  className="pt-2.5"
                  value={field.value}
                  onChange={e => {
                    const v = e.target.value;
                    field.onChange(v);
                    if (v === '0') {
                      setValue('children_num', '', { shouldDirty: true });
                      clearErrors('children_num');
                    } else if (v === '1') {
                      clearErrors('children_num');
                    }
                  }}
                >
                  <AppRadio value="1">有り</AppRadio>
                  <AppRadio value="0">無し</AppRadio>
                </AppRadioGroup>
              )}
            />

            <div className="flex items-start">
              <div>
                <Controller
                  name="children_num"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      className="mb-0"
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                    >
                      <AppTextField
                        {...field}
                        placeholder="人数"
                        disabled={childrenNumDisabled}
                        status={fieldState.error ? 'error' : undefined}
                        maxLength={2}
                        className="w-[100px]"
                        onChange={e => {
                          field.onChange(e);
                          clearErrors('children_num');
                        }}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 pt-1 text-sm text-gray-700">職業</div>
          <Controller
            name="dm_occupation_type_id"
            control={control}
            render={({ field }) => (
              <AppRadioGroup
                className="w-full max-w-xl pt-1"
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
              >
                {occupationTypes.map(t => (
                  <AppRadio key={t.id} value={String(t.id)}>
                    {t.name}
                  </AppRadio>
                ))}
              </AppRadioGroup>
            )}
          />
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 text-sm text-gray-700">勤務先</div>
          <div className="w-full max-w-xl">
            <Controller
              name="company_name"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    className="w-full"
                    maxLength={255}
                    status={fieldState.error ? 'error' : undefined}
                    onChange={e => {
                      field.onChange(e);
                      clearErrors('company_name');
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 text-sm text-gray-700">携帯番号</div>
          <div className="w-full max-w-xl">
            <Controller
              name="mobilephone"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    className="w-full"
                    maxLength={13}
                    status={fieldState.error ? 'error' : undefined}
                    onChange={e => {
                      field.onChange(e);
                      clearErrors('mobilephone');
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 text-sm text-gray-700">メールアドレス</div>
          <div className="w-full max-w-xl">
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <AppTextField
                    {...field}
                    className="w-full"
                    maxLength={255}
                    status={fieldState.error ? 'error' : undefined}
                    onChange={e => {
                      field.onChange(e);
                      clearErrors('email');
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-32 pt-1 text-sm text-gray-700">当院を知った理由</div>
          <Controller
            name="dm_reason_type_id"
            control={control}
            render={({ field }) => (
              <AppRadioGroup
                className="w-full max-w-xl pt-1"
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
              >
                {reasonTypes.map(t => (
                  <AppRadio key={t.id} value={String(t.id)}>
                    {t.name}
                  </AppRadio>
                ))}
              </AppRadioGroup>
            )}
          />
        </div>

      </div>

      <div className="pt-4">
        <div className="mb-2 text-sm text-gray-700">患者情報検索用フリーワード</div>
        <div className="grid grid-cols-1 gap-3 ml-32 lg:grid-cols-2">
          {freeKeys.map(([label, key]) => (
            <div key={key} className="flex items-center gap-2">
              <AppFormLabel label={label} className="w-10 text-sm text-gray-700" />
              <Controller
                name={key}
                control={control}
                render={({ field }) => (
                  <AppTextField className="w-full max-w-xl" maxLength={255} {...field} />
                )}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4">
        <div className="flex items-start gap-4">
          <div className="w-32 text-sm text-gray-700">備考</div>
          <div className="flex-1">
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => <AppTextArea rows={5} maxLength={255} {...field} />}
            />
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default PatientDmInfoSection;
